import {
  OrderStatusReasonUnspecified,
  StatusCodeBadRequest,
  StatusCodeForbidden,
  StatusCodeNotFound,
  StatusCodeSuccess,
  StatusCodeTooManyRequests,
  StatusInternalServerError,
  type StatusResponse,
} from '@/api/spec.ts';
import { captureError } from '../../../utils/sentryHelpers';
import { useOpenOrdersStore } from '../OpenOrdersStore.ts';

/** Protocol-level rejections never become an `OrdersUpdate` — unhandled, they just went quiet. */
const STATUS_CODE_MESSAGE: Record<number, string> = {
  [StatusCodeBadRequest]: 'The exchange rejected this request.',
  [StatusCodeForbidden]: 'This trading session is no longer authorized. Please reload the page.',
  [StatusCodeNotFound]: 'The exchange could not find what this request referred to.',
  [StatusCodeTooManyRequests]: 'Too many requests — please wait a moment and try again.',
  [StatusInternalServerError]: 'The exchange had an internal error. Please try again.',
};

/**
 * Constant, and worded around every filter in `captureError` — which drops an error whose message
 * contains `404` / `not found` / `http 429` *or* any of `REJECTION_PATTERNS` (`sentryHelpers.ts`).
 * "request rejected" is one of those patterns, so the obvious phrasing silences this whole channel;
 * the test asserts against the real `captureError`, not a mock, so a reword that trips a filter
 * fails there. The status code goes in the extras.
 */
const SENTRY_MESSAGE = 'Exchange refused a trading request';

export function handleTradingStatusResponse(response: StatusResponse) {
  const code = response.status?.code;
  if (code == null || code === StatusCodeSuccess) return;

  // `cid` is the `sn` of the frame that caused this. Unresolved means it belongs to something other
  // than an order request — auth, a subscription — which is worth reporting too.
  const requestId = response.cid != null ? useOpenOrdersStore.getState().requestByFrameSeqNo.get(response.cid) : undefined;

  captureError(new Error(SENTRY_MESSAGE), 'ws_status_response', {
    statusCode: code,
    statusError: response.status?.error ?? null,
    cid: response.cid ?? null,
    requestId: requestId ?? null,
    sessionId: response.ses ?? null,
  });

  if (requestId == null) return;

  const detail = STATUS_CODE_MESSAGE[code] ?? 'The exchange rejected this request.';
  // The server's text is the only thing that names the actual cause. Parenthesised the way
  // `getOrderErrorInfo` appends its context.
  const message = response.status?.error ? `${detail} (${response.status.error})` : detail;

  // Reports the request itself, so a rejection the posting slot has moved past still surfaces
  // instead of clobbering a newer one. Retiring it is all the disarming there is.
  useOpenOrdersStore.getState().failInFlightRequest(requestId, OrderStatusReasonUnspecified, message);
}
